import React from 'react'
import {
  DateField,
  FunctionField,
  Record,
  ReferenceField,
  TextField,
  useTranslate,
} from 'react-admin'
import { ImportProperties } from '../../../core/enum/ImportProperties'
import { ResourceName } from '../../../core/ResourceName'
import { TicketImportMappingDto } from '../../../core/dto/ticket-import-mapping.dto'
import { Authority } from '../../../core/auth/Authority'
import { useHasAuthority } from '../../hooks/useHasAuthority'

// fix for ReferenceFields linking to ticket-import-template
const linkToType = (record: Record, reference: string) => {
  const { staticValue } = record

  return `/${reference}/${staticValue}/show`
}

const MappingTicketImportFunctionField = ({ ...props }) => {
  const { objectTypeName, ...rest } = props
  const translate = useTranslate()

  return (
    <FunctionField<TicketImportMappingDto>
      {...rest}
      source="staticValue"
      render={(mapping?: TicketImportMappingDto) =>
        translate(`resources.enums.${objectTypeName}.${mapping?.staticValue}`)
      }
    />
  )
}

const MappingTicketImportReferenceFunctionField = ({ ...props }) => {
  const { reference, authority, ...rest } = props
  const hasAuthority = useHasAuthority()

  return hasAuthority(authority) ? (
    <ReferenceField
      {...rest}
      source="staticValue"
      reference={reference}
      link={linkToType}
    >
      <FunctionField<any>
        source="name"
        render={(record?: any) => `${record?.id}. ${record?.name}`}
      />
    </ReferenceField>
  ) : (
    <TextField {...rest} source="staticValue" />
  )
}

export const MappingStaticValueField = ({ ...props }) => {
  const { record: template, ...rest } = props
  const { property } = template

  switch (property) {
    case ImportProperties.TICKET_TYPE:
      return (
        <MappingTicketImportFunctionField
          {...rest}
          objectTypeName="ticketType"
        />
      )
    case ImportProperties.BOOKING_TYPE:
      return (
        <MappingTicketImportFunctionField
          {...rest}
          objectTypeName="bookingType"
        />
      )
    case ImportProperties.DISABLED:
      return (
        <MappingTicketImportFunctionField
          {...rest}
          objectTypeName="bookingDisabled"
        />
      )
    case ImportProperties.BLOCK_ID:
      return (
        <MappingTicketImportReferenceFunctionField
          {...rest}
          reference={ResourceName.BLOCKS}
          authority={Authority.VIEW_BLOCKS}
        />
      )
    case ImportProperties.VARIANT_ID:
      return (
        <MappingTicketImportReferenceFunctionField
          {...rest}
          reference={ResourceName.VARIANTS}
          authority={Authority.VIEW_VARIANTS}
        />
      )
    case ImportProperties.EVENT_ID:
      return (
        <MappingTicketImportReferenceFunctionField
          {...rest}
          reference={ResourceName.EVENTS}
          authority={Authority.VIEW_EVENTS}
        />
      )
    case ImportProperties.BOOKING_ACTIVE_FROM:
    case ImportProperties.BOOKING_ACTIVE_TO:
      return (
        <DateField
          {...rest}
          source="staticValue"
          options={{
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
          {...props}
        />
      )
    default:
      return <TextField {...rest} source="staticValue" />
  }
}
